import { useEvent } from '@emerald-works/react-event-bus-client'
import { useCallback } from 'react'
import { NOTIFICATIONS_API_ENABLED } from '../constants'

export const NOTIFICATION_EVENT_TYPE = {
  PANEL_OPENED: 'panelOpened',
  PANEL_CLOSED: 'panelClosed',
  ITEM_CLICKED: 'itemClicked',
  MARK_ALL_READ: 'markAllRead',
  TAB_CHANGED: 'tabChanged',
  VIEW_ALL_CLICKED: 'viewAllClicked',
  RETRY_CLICKED: 'retryClicked',
  LOAD_MORE_CLICKED: 'loadMoreClicked'
}

/**
 * Analytics for the notification centre, following the shape used by
 * `useTrackHomeEnterpriseEvent`: one event name, an `eventType` discriminator
 * and a free-form `data` bag.
 */
export const useTrackNotificationEvent = () => {
  const [notificationEvent] = useEvent([{ eventName: 'notificationEvent' }])

  // Held back until the backend registers `notificationEvent` (listed in the
  // TODO(API) block in reducers/notifications/event-bus.js) — firing an event
  // nothing consumes only puts noise on the bus. Every call site stays wired,
  // so the flag flip turns analytics on with it.
  const trackEvent = useCallback(
    (eventType, data = {}) => {
      if (!NOTIFICATIONS_API_ENABLED) return
      notificationEvent.trigger({ eventType, data })
    },
    [notificationEvent]
  )

  const trackPanelOpened = useCallback(
    unreadCount => trackEvent(NOTIFICATION_EVENT_TYPE.PANEL_OPENED, { unreadCount }),
    [trackEvent]
  )

  const trackPanelClosed = useCallback(
    () => trackEvent(NOTIFICATION_EVENT_TYPE.PANEL_CLOSED),
    [trackEvent]
  )

  const trackItemClicked = useCallback(
    notification =>
      trackEvent(NOTIFICATION_EVENT_TYPE.ITEM_CLICKED, {
        id: notification?.id,
        type: notification?.type,
        wasUnread: !notification?.read
      }),
    [trackEvent]
  )

  const trackMarkAllRead = useCallback(
    unreadCount => trackEvent(NOTIFICATION_EVENT_TYPE.MARK_ALL_READ, { unreadCount }),
    [trackEvent]
  )

  const trackTabChanged = useCallback(
    tab => trackEvent(NOTIFICATION_EVENT_TYPE.TAB_CHANGED, { tab }),
    [trackEvent]
  )

  const trackViewAllClicked = useCallback(
    () => trackEvent(NOTIFICATION_EVENT_TYPE.VIEW_ALL_CLICKED),
    [trackEvent]
  )

  const trackRetryClicked = useCallback(
    () => trackEvent(NOTIFICATION_EVENT_TYPE.RETRY_CLICKED),
    [trackEvent]
  )

  const trackLoadMoreClicked = useCallback(
    loadedCount => trackEvent(NOTIFICATION_EVENT_TYPE.LOAD_MORE_CLICKED, { loadedCount }),
    [trackEvent]
  )

  return {
    trackEvent,
    trackPanelOpened,
    trackPanelClosed,
    trackItemClicked,
    trackMarkAllRead,
    trackTabChanged,
    trackViewAllClicked,
    trackRetryClicked,
    trackLoadMoreClicked
  }
}

export default useTrackNotificationEvent
